import { unitsJson } from "@corca/data";
import { nodeJsonPlainText } from "@corca/sympy";

import type { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import type { Unit } from "@corca/data";

export type ConvertCandidate = { unit: Unit; display: string };

function unitDisplayText(unit: Unit): string | null {
  const first = unit.children[0];
  if (first?.type !== "plainText") return null;
  return first.props?.value ?? null;
}

export function getConvertUnitInfo(
  node: NumberNodeModel,
): { currentUnit: Unit; candidates: ConvertCandidate[] } | null {
  if (node.power || node.subscript) return null;

  const inline = node.unitNode?.inlineNode;
  if (!inline) return null;

  const display = nodeJsonPlainText(inline.toJSON());
  const currentUnit = unitsJson.find((u) => unitDisplayText(u) === display);
  if (!currentUnit?.dimension) return null;

  const candidates = unitsJson
    .filter((u) => u !== currentUnit && u.dimension === currentUnit.dimension)
    .flatMap((unit) => {
      const candidateDisplay = unitDisplayText(unit);
      return candidateDisplay ? [{ unit, display: candidateDisplay }] : [];
    });

  return candidates.length > 0 ? { currentUnit, candidates } : null;
}

export function filterConvertCandidates(
  candidates: ConvertCandidate[],
  query: string,
): ConvertCandidate[] {
  const q = query.trim().toLowerCase();
  if (!q) return candidates;

  const score = ({ unit, display }: ConvertCandidate): number => {
    const texts = [display, unit.name ?? "", ...(unit.keywords ?? [])].map((t) => t.toLowerCase());
    if (texts[0] === q) return 0;
    if (texts.some((t) => t.startsWith(q))) return 1;
    if (texts.some((t) => t.includes(q))) return 2;
    return 3;
  };

  return candidates
    .map((candidate) => ({ candidate, score: score(candidate) }))
    .filter(({ score: s }) => s < 3)
    .sort((a, b) => a.score - b.score)
    .map(({ candidate }) => candidate);
}
